import React, { useRef, useEffect, useState, useCallback } from 'react';
import { GameEngine, GAME_CONFIG, castFlashlight } from './gameEngine';
import { SKINS, generateCharacterSprite, generateWallTexture, generateFloorTexture, generateBulletSprite, generateCrosshair, generateSkinPreview } from './spriteGenerator';

const BlackBoxGame = ({ socket, popupId, user, onExit }) => {
    const canvasRef = useRef(null);
    const engineRef = useRef(null);
    const keysRef = useRef({});
    const mouseRef = useRef({ x: 0, y: 0, angle: 0 });
    const animFrameRef = useRef(null);
    // Reusable offscreen canvases — created ONCE
    const lightCanvasRef = useRef(null);
    const sceneCanvasRef = useRef(null);

    const [gameState, setGameState] = useState('lobby');
    const [players, setPlayers] = useState([]);
    const [countdown, setCountdown] = useState(null);
    const [results, setResults] = useState(null);
    const [timeDisplay, setTimeDisplay] = useState(GAME_CONFIG.GAME_DURATION);
    const [localHP, setLocalHP] = useState(GAME_CONFIG.PLAYER_MAX_HP);
    const [localKills, setLocalKills] = useState(0);
    const [killFeed, setKillFeed] = useState([]);
    const [selectedSkin, setSelectedSkin] = useState(0); // index into SKINS array
    // Cached texture canvases
    const wallTexRef = useRef(null);
    const floorTexRef = useRef(null);
    const floorPatternRef = useRef(null);

    // Initialize engine + offscreen canvases
    useEffect(() => {
        engineRef.current = new GameEngine();
        engineRef.current.localPlayerId = user.id;

        const lc = document.createElement('canvas');
        lc.width = GAME_CONFIG.MAP_WIDTH;
        lc.height = GAME_CONFIG.MAP_HEIGHT;
        lightCanvasRef.current = lc;

        const sc = document.createElement('canvas');
        sc.width = GAME_CONFIG.MAP_WIDTH;
        sc.height = GAME_CONFIG.MAP_HEIGHT;
        sceneCanvasRef.current = sc;

        return () => {
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
            lightCanvasRef.current = null;
            sceneCanvasRef.current = null;
        };
    }, [user.id]);

    // Screen flash for hit feedback
    const screenFlashRef = useRef(0);

    // Socket events
    useEffect(() => {
        if (!socket) return;

        socket.on('game_player_joined', (data) => {
            console.log('[BlackBox] player joined:', data);
            const engine = engineRef.current;
            if (!engine.players.has(data.id)) {
                engine.addPlayer(data.id, data.username, data.colorIndex, data.skinId);
            } else {
                // Update skinId on existing player (e.g. skin changed)
                const existing = engine.players.get(data.id);
                if (data.skinId) existing.skinId = data.skinId;
            }
            setPlayers(Array.from(engine.players.values()));
        });

        socket.on('game_player_left', (data) => {
            console.log('[BlackBox] player left:', data);
            engineRef.current.removePlayer(data.id);
            setPlayers(Array.from(engineRef.current.players.values()));
        });

        socket.on('game_players_list', (list) => {
            console.log('[BlackBox] players list:', list);
            const engine = engineRef.current;
            list.forEach(p => {
                if (!engine.players.has(p.id)) {
                    engine.addPlayer(p.id, p.username, p.colorIndex, p.skinId);
                } else {
                    // Update skinId on existing player
                    const existing = engine.players.get(p.id);
                    if (p.skinId) existing.skinId = p.skinId;
                }
            });
            setPlayers(Array.from(engine.players.values()));
        });

        socket.on('game_countdown', (count) => {
            setCountdown(count);
            if (count === 0) {
                setCountdown(null);
                setGameState('playing');
                engineRef.current.startGame();
                startGameLoop();
            }
        });

        socket.on('game_player_move', (data) => {
            if (data.id !== user.id) {
                engineRef.current.updateRemotePlayer(data.id, data);
            }
        });

        socket.on('game_bullet_fired', (data) => {
            console.log('[BlackBox] bullet received from:', data.ownerId, 'local:', user.id);
            if (data.ownerId !== user.id) {
                engineRef.current.addRemoteBullet(data);
                console.log('[BlackBox] remote bullet added, total bullets:', engineRef.current.bullets.length);
            }
        });

        socket.on('game_player_hit', (data) => {
            console.log('[BlackBox] HIT EVENT:', data);
            const engine = engineRef.current;
            const victim = engine.players.get(data.victimId);
            const attacker = engine.players.get(data.attackerId);
            if (victim) {
                victim.hp = data.victimHP;
                victim.alive = data.victimAlive;
                // Big particle burst from victim
                engine.spawnParticles(victim.x, victim.y, '#fff', 15);
                // Screen flash if WE got hit
                if (data.victimId === user.id) {
                    screenFlashRef.current = 30; // 30 frames of red flash
                    console.log('[BlackBox] WE GOT HIT! HP:', data.victimHP);
                }
            }
            if (attacker) {
                attacker.kills = data.attackerKills;
            }
            if (!data.victimAlive && attacker && victim) {
                engine.addKillFeedEntry(attacker.username, victim.username);
                setKillFeed([...engine.killFeed]);
                engine.spawnParticles(victim.x, victim.y, '#fff', 30); // big death burst
            }
        });

        socket.on('game_ended', (data) => {
            setGameState('ended');
            engineRef.current.gameState = 'ended';
            setResults(data);
        });

        return () => {
            socket.off('game_player_joined');
            socket.off('game_player_left');
            socket.off('game_players_list');
            socket.off('game_countdown');
            socket.off('game_player_move');
            socket.off('game_bullet_fired');
            socket.off('game_player_hit');
            socket.off('game_ended');
        };
    }, [socket, user.id]);

    // Join lobby — only fires when socket/popup/user change, NOT on skin change
    useEffect(() => {
        if (socket && popupId) {
            console.log('[BlackBox] Socket connected?', socket.connected, 'Socket ID:', socket.id);
            console.log('[BlackBox] Joining game, popupId:', popupId, 'userId:', user.id);
            socket.emit('game_join', { popup_id: popupId, user_id: user.id, username: user.username, skinId: SKINS[selectedSkin].id });
            engineRef.current.localSkinId = SKINS[selectedSkin].id;
        } else {
            console.log('[BlackBox] Socket NOT available! socket:', !!socket, 'popupId:', popupId);
        }
        return () => {
            if (socket) socket.emit('game_leave', { popup_id: popupId, user_id: user.id });
        };
        // eslint-disable-next-line
    }, [socket, popupId, user.id]);

    // Update local player skin when carousel changes (no re-join needed)
    useEffect(() => {
        const engine = engineRef.current;
        const skinId = SKINS[selectedSkin].id;
        engine.localSkinId = skinId;
        // Update existing local player if already added
        const localPlayer = engine.players.get(user.id);
        if (localPlayer) {
            localPlayer.skinId = skinId;
        }
    }, [selectedSkin, user.id]);

    // Keyboard
    useEffect(() => {
        const handleKeyDown = (e) => {
            const key = e.key.toLowerCase();
            keysRef.current[key] = true;
            if (['w', 'a', 's', 'd', ' ', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(key)) {
                e.preventDefault();
            }
            if (key === 'escape') onExit();
        };
        const handleKeyUp = (e) => {
            keysRef.current[e.key.toLowerCase()] = false;
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [onExit]);

    // Mouse
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const handleMouseMove = (e) => {
            const rect = canvas.getBoundingClientRect();
            const scaleX = GAME_CONFIG.MAP_WIDTH / rect.width;
            const scaleY = GAME_CONFIG.MAP_HEIGHT / rect.height;
            mouseRef.current.x = (e.clientX - rect.left) * scaleX;
            mouseRef.current.y = (e.clientY - rect.top) * scaleY;

            const player = engineRef.current?.players.get(user.id);
            if (player) {
                mouseRef.current.angle = Math.atan2(
                    mouseRef.current.y - player.y,
                    mouseRef.current.x - player.x
                );
            }
        };

        const handleClick = (e) => {
            e.preventDefault();
            if (gameState !== 'playing') return;
            const engine = engineRef.current;
            const bulletData = engine.shoot(user.id);
            if (bulletData && socket) {
                console.log('[BlackBox] SHOOTING! bullet:', bulletData);
                socket.emit('game_shoot', {
                    popup_id: popupId,
                    ...bulletData,
                    ownerId: user.id
                });
            }
        };

        canvas.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('click', handleClick);
        return () => {
            canvas.removeEventListener('mousemove', handleMouseMove);
            canvas.removeEventListener('click', handleClick);
        };
    }, [gameState, popupId, socket, user.id]);

    // Game loop
    const startGameLoop = useCallback(() => {
        const loop = () => {
            const engine = engineRef.current;
            if (!engine || engine.gameState !== 'playing') return;

            const pos = engine.updateLocalPlayer(keysRef.current, mouseRef.current.angle);
            if (pos && socket) {
                socket.emit('game_move', {
                    popup_id: popupId,
                    id: user.id,
                    x: pos.x, y: pos.y, angle: pos.angle
                });
            }

            engine.updateBullets();

            // Shooter-authoritative: check both local→remote AND remote→local hits
            const hits = engine.checkAllHits();
            for (const hit of hits) {
                if (socket) {
                    console.log('[BlackBox] HIT DETECTED:', hit);
                    socket.emit('game_hit', {
                        popup_id: popupId,
                        victimId: hit.victimId,
                        attackerId: hit.attackerId,
                        type: hit.type
                    });
                }
                // Screen flash when WE get hit
                if (hit.victimId === user.id) {
                    screenFlashRef.current = 30;
                }
            }

            engine.updateParticles();
            engine.updateDust();
            engine.spawnPowerup();
            engine.updateTime();

            const localPlayer = engine.players.get(user.id);
            if (localPlayer) {
                setLocalHP(localPlayer.hp);
                setLocalKills(localPlayer.kills);
            }
            setTimeDisplay(Math.ceil(engine.timeRemaining));
            setKillFeed([...engine.killFeed]);

            if (engine.gameState === 'ended') {
                setGameState('ended');
                const winner = engine.getWinner();
                setResults({
                    winner: winner?.username || 'No one',
                    players: Array.from(engine.players.values()).map(p => ({
                        username: p.username,
                        kills: p.kills,
                        alive: p.alive,
                        color: p.color
                    }))
                });
                return;
            }

            render(engine);
            animFrameRef.current = requestAnimationFrame(loop);
        };

        animFrameRef.current = requestAnimationFrame(loop);
    }, [socket, popupId, user.id]);

    // === RENDERER — Atmospheric dark chamber with raycasted flashlight ===
    const render = useCallback((engine) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const localPlayer = engine.players.get(engine.localPlayerId);
        const W = GAME_CONFIG.MAP_WIDTH;
        const H = GAME_CONFIG.MAP_HEIGHT;

        // Reuse offscreen canvases
        const lightCanvas = lightCanvasRef.current;
        const sceneCanvas = sceneCanvasRef.current;
        if (!lightCanvas || !sceneCanvas) return;
        const lightCtx = lightCanvas.getContext('2d');
        const sc = sceneCanvas.getContext('2d');

        // ============================================================
        // 1. LIGHT MASK — everything starts in pure darkness
        // ============================================================
        lightCtx.clearRect(0, 0, W, H);
        lightCtx.fillStyle = '#000';
        lightCtx.fillRect(0, 0, W, H);
        lightCtx.globalCompositeOperation = 'lighter';

        // Player's directional flashlight cone with raycasted shadows
        if (localPlayer && localPlayer.alive) {
            const range = localPlayer.flashlightRange;
            const flicker = 0.96 + 0.04 * Math.sin(Date.now() * 0.008);

            // Cast rays to build visibility polygon
            const visPoints = castFlashlight(
                localPlayer.x, localPlayer.y,
                localPlayer.angle,
                GAME_CONFIG.FLASHLIGHT_CONE,
                range * flicker,
                engine.obstacles
            );

            // Draw visibility polygon with gradient
            if (visPoints.length > 1) {
                // Create cone gradient
                const gx = localPlayer.x + Math.cos(localPlayer.angle) * range * 0.5;
                const gy = localPlayer.y + Math.sin(localPlayer.angle) * range * 0.5;
                const gr = lightCtx.createRadialGradient(
                    localPlayer.x, localPlayer.y, 5,
                    localPlayer.x, localPlayer.y, range * flicker
                );
                gr.addColorStop(0, 'rgba(255,255,255,1)');
                gr.addColorStop(0.3, 'rgba(255,255,255,0.8)');
                gr.addColorStop(0.6, 'rgba(255,255,255,0.4)');
                gr.addColorStop(0.85, 'rgba(255,255,255,0.1)');
                gr.addColorStop(1, 'rgba(255,255,255,0)');

                lightCtx.fillStyle = gr;
                lightCtx.beginPath();
                lightCtx.moveTo(localPlayer.x, localPlayer.y);
                for (const p of visPoints) {
                    lightCtx.lineTo(p.x, p.y);
                }
                lightCtx.closePath();
                lightCtx.fill();
            }

            // Tiny ambient glow around player (so they can see their feet)
            const ambGr = lightCtx.createRadialGradient(
                localPlayer.x, localPlayer.y, 0,
                localPlayer.x, localPlayer.y, GAME_CONFIG.AMBIENT_GLOW
            );
            ambGr.addColorStop(0, 'rgba(255,255,255,0.5)');
            ambGr.addColorStop(0.5, 'rgba(255,255,255,0.15)');
            ambGr.addColorStop(1, 'rgba(255,255,255,0)');
            lightCtx.fillStyle = ambGr;
            lightCtx.beginPath();
            lightCtx.arc(localPlayer.x, localPlayer.y, GAME_CONFIG.AMBIENT_GLOW, 0, Math.PI * 2);
            lightCtx.fill();
        }

        // Ambient lantern lights (warm dim glows in the map)
        engine.ambientLights.forEach(al => {
            const pulse = 0.85 + 0.15 * Math.sin(Date.now() * 0.002 + al.x * 0.1);
            const gr = lightCtx.createRadialGradient(al.x, al.y, 0, al.x, al.y, al.radius * pulse);
            gr.addColorStop(0, `rgba(255,255,255,${al.intensity * pulse})`);
            gr.addColorStop(0.5, `rgba(255,255,255,${al.intensity * pulse * 0.3})`);
            gr.addColorStop(1, 'rgba(255,255,255,0)');
            lightCtx.fillStyle = gr;
            lightCtx.beginPath();
            lightCtx.arc(al.x, al.y, al.radius * pulse, 0, Math.PI * 2);
            lightCtx.fill();
        });

        // Bullet light
        engine.bullets.forEach(b => {
            const gr = lightCtx.createRadialGradient(b.x, b.y, 0, b.x, b.y, GAME_CONFIG.BULLET_LIGHT_RADIUS);
            gr.addColorStop(0, 'rgba(255,255,255,0.6)');
            gr.addColorStop(0.5, 'rgba(255,255,255,0.2)');
            gr.addColorStop(1, 'rgba(255,255,255,0)');
            lightCtx.fillStyle = gr;
            lightCtx.beginPath();
            lightCtx.arc(b.x, b.y, GAME_CONFIG.BULLET_LIGHT_RADIUS, 0, Math.PI * 2);
            lightCtx.fill();
        });

        // Explosion particles emit light
        engine.particles.forEach(p => {
            if (p.life / p.maxLife > 0.4) {
                const gr = lightCtx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 12);
                gr.addColorStop(0, `rgba(255,255,255,${0.15 * (p.life / p.maxLife)})`);
                gr.addColorStop(1, 'rgba(255,255,255,0)');
                lightCtx.fillStyle = gr;
                lightCtx.beginPath();
                lightCtx.arc(p.x, p.y, 12, 0, Math.PI * 2);
                lightCtx.fill();
            }
        });

        lightCtx.globalCompositeOperation = 'source-over';

        // ============================================================
        // 2. SCENE — draw everything in monochrome
        // ============================================================
        sc.clearRect(0, 0, W, H);
        sc.fillStyle = '#0a0a0a';
        sc.fillRect(0, 0, W, H);

        // Floor texture — tiled procedural concrete
        if (!floorPatternRef.current) {
            const floorTex = generateFloorTexture(128, 128);
            floorPatternRef.current = sc.createPattern(floorTex, 'repeat');
        }
        if (floorPatternRef.current) {
            sc.fillStyle = floorPatternRef.current;
            sc.fillRect(0, 0, W, H);
        }

        // Draw obstacles — textured metal panels
        const wallTex = generateWallTexture(64, 64);
        const wallPattern = sc.createPattern(wallTex, 'repeat');
        engine.obstacles.forEach(obs => {
            // Textured wall fill
            sc.save();
            sc.fillStyle = wallPattern || '#1e2328';
            sc.fillRect(obs.x, obs.y, obs.w, obs.h);
            sc.restore();

            // Top edge highlight
            sc.strokeStyle = 'rgba(255,255,255,0.15)';
            sc.lineWidth = 1;
            sc.beginPath();
            sc.moveTo(obs.x, obs.y);
            sc.lineTo(obs.x + obs.w, obs.y);
            sc.stroke();

            // Left edge
            sc.strokeStyle = 'rgba(255,255,255,0.08)';
            sc.beginPath();
            sc.moveTo(obs.x, obs.y);
            sc.lineTo(obs.x, obs.y + obs.h);
            sc.stroke();

            // Bottom/right shadow
            sc.strokeStyle = 'rgba(0,0,0,0.5)';
            sc.lineWidth = 1.5;
            sc.beginPath();
            sc.moveTo(obs.x + obs.w, obs.y);
            sc.lineTo(obs.x + obs.w, obs.y + obs.h);
            sc.lineTo(obs.x, obs.y + obs.h);
            sc.stroke();
        });

        // Ambient lantern sources — small warm glows
        engine.ambientLights.forEach(al => {
            const pulse = 0.7 + 0.3 * Math.sin(Date.now() * 0.003 + al.x * 0.05);
            // Lantern body
            sc.fillStyle = `rgba(200,180,140,${0.6 * pulse})`;
            sc.beginPath();
            sc.arc(al.x, al.y, 3, 0, Math.PI * 2);
            sc.fill();
            // Surrounding warm glow
            sc.shadowColor = 'rgba(200,180,140,0.3)';
            sc.shadowBlur = 8;
            sc.fillStyle = `rgba(220,200,160,${0.3 * pulse})`;
            sc.beginPath();
            sc.arc(al.x, al.y, 2, 0, Math.PI * 2);
            sc.fill();
            sc.shadowBlur = 0;
        });

        // Draw powerups — monochrome glowing orbs
        engine.powerups.forEach(pw => {
            const pulse = 0.5 + 0.5 * Math.sin(Date.now() * 0.004 + pw.pulsePhase);
            sc.save();
            if (pw.type === 'health') {
                sc.shadowColor = '#fff';
                sc.shadowBlur = 8 * pulse;
                sc.fillStyle = `rgba(255,255,255,${0.5 + pulse * 0.3})`;
                sc.beginPath();
                sc.arc(pw.x, pw.y, GAME_CONFIG.POWERUP_RADIUS, 0, Math.PI * 2);
                sc.fill();
                sc.shadowBlur = 0;
                sc.strokeStyle = '#000';
                sc.lineWidth = 1.5;
                sc.beginPath();
                sc.moveTo(pw.x - 3, pw.y); sc.lineTo(pw.x + 3, pw.y);
                sc.moveTo(pw.x, pw.y - 3); sc.lineTo(pw.x, pw.y + 3);
                sc.stroke();
            } else {
                sc.shadowColor = '#ddd';
                sc.shadowBlur = 10 * pulse;
                sc.fillStyle = `rgba(230,230,200,${0.5 + pulse * 0.3})`;
                sc.beginPath();
                sc.arc(pw.x, pw.y, GAME_CONFIG.POWERUP_RADIUS, 0, Math.PI * 2);
                sc.fill();
                sc.shadowBlur = 0;
                sc.fillStyle = '#000';
                sc.font = 'bold 8px sans-serif';
                sc.textAlign = 'center';
                sc.textBaseline = 'middle';
                sc.fillText('★', pw.x, pw.y + 1);
            }
            sc.restore();
        });

        // Draw dust motes — only visible when lit
        engine.dustMotes.forEach(d => {
            sc.fillStyle = `rgba(255,255,255,${d.alpha * 0.5})`;
            sc.beginPath();
            sc.arc(d.x, d.y, d.radius, 0, Math.PI * 2);
            sc.fill();
        });

        // Draw bullet trails + sprite bullets
        engine.bullets.forEach(bullet => {
            // Trail glow
            for (let i = 0; i < bullet.trail.length; i++) {
                const pos = bullet.trail[i];
                const alpha = ((i + 1) / bullet.trail.length) * 0.35;
                sc.fillStyle = `rgba(255,255,255,${alpha})`;
                sc.beginPath();
                sc.arc(pos.x, pos.y, GAME_CONFIG.BULLET_RADIUS * 0.5, 0, Math.PI * 2);
                sc.fill();
            }
            // Bullet sprite (glowing orb)
            const bulletSprite = generateBulletSprite(bullet.color, 16);
            sc.drawImage(bulletSprite, bullet.x - 8, bullet.y - 8, 16, 16);
        });

        // Draw players — sprite-based silhouettes with skin accents
        engine.players.forEach(player => {
            if (!player.alive) return;

            const R = GAME_CONFIG.PLAYER_RADIUS;
            const skin = SKINS.find(s => s.id === player.skinId) || SKINS[0];

            // Trail / footprint glow behind player
            sc.fillStyle = skin.trail;
            sc.beginPath();
            sc.arc(player.x - player.vx * 3, player.y - player.vy * 3, R * 0.6, 0, Math.PI * 2);
            sc.fill();

            // Drop shadow
            sc.fillStyle = 'rgba(0,0,0,0.5)';
            sc.beginPath();
            sc.ellipse(player.x, player.y + R + 2, R * 0.8, 3, 0, 0, Math.PI * 2);
            sc.fill();

            // Render character sprite (rotated to face angle)
            sc.save();
            sc.translate(player.x, player.y);
            sc.rotate(player.angle + Math.PI / 2); // sprite faces up, rotate to match angle
            const spriteSize = R * 2.8;
            const charSprite = generateCharacterSprite(skin, Math.round(spriteSize));
            sc.drawImage(charSprite, -spriteSize / 2, -spriteSize / 2, spriteSize, spriteSize);
            sc.restore();

            // Gun barrel for local player
            if (player.id === engine.localPlayerId) {
                const tipX = player.x + Math.cos(player.angle) * (R + 8);
                const tipY = player.y + Math.sin(player.angle) * (R + 8);
                sc.strokeStyle = skin.glow + '40';
                sc.lineWidth = 2;
                sc.beginPath();
                sc.moveTo(
                    player.x + Math.cos(player.angle) * R,
                    player.y + Math.sin(player.angle) * R
                );
                sc.lineTo(tipX, tipY);
                sc.stroke();
                // Muzzle dot
                sc.fillStyle = skin.glow + '80';
                sc.beginPath();
                sc.arc(tipX, tipY, 2, 0, Math.PI * 2);
                sc.fill();
            }

            // Username with skin accent color
            sc.fillStyle = skin.glow + '90';
            sc.font = 'bold 8px monospace';
            sc.textAlign = 'center';
            sc.fillText(player.username.toUpperCase(), player.x, player.y - R - 12);

            // HP pips in skin glow color
            const pipSpacing = 7;
            const pipStartX = player.x - ((GAME_CONFIG.PLAYER_MAX_HP - 1) * pipSpacing) / 2;
            for (let i = 0; i < GAME_CONFIG.PLAYER_MAX_HP; i++) {
                const px = pipStartX + i * pipSpacing;
                const py = player.y - R - 5;
                if (i < player.hp) {
                    sc.fillStyle = skin.glow;
                    sc.shadowColor = skin.glow;
                    sc.shadowBlur = 4;
                } else {
                    sc.fillStyle = 'rgba(255,255,255,0.1)';
                    sc.shadowBlur = 0;
                }
                sc.beginPath();
                sc.arc(px, py, 2.5, 0, Math.PI * 2);
                sc.fill();
                sc.shadowBlur = 0;
            }
        });

        // Draw particles
        engine.particles.forEach(p => {
            const lifeRatio = p.life / p.maxLife;
            sc.globalAlpha = lifeRatio;
            sc.fillStyle = p.color;
            sc.beginPath();
            sc.arc(p.x, p.y, p.radius * lifeRatio, 0, Math.PI * 2);
            sc.fill();
        });
        sc.globalAlpha = 1;

        // HUD on canvas — kill count near player
        if (localPlayer && localPlayer.alive) {
            sc.fillStyle = 'rgba(255,255,255,0.3)';
            sc.font = '8px monospace';
            sc.textAlign = 'center';
            sc.fillText(`kills:${localPlayer.kills}`, localPlayer.x, localPlayer.y + GAME_CONFIG.PLAYER_RADIUS + 18);
        }

        // Timer top center
        sc.fillStyle = 'rgba(255,255,255,0.3)';
        sc.font = 'bold 12px monospace';
        sc.textAlign = 'center';
        const mins = Math.floor(engine.timeRemaining / 60);
        const secs = Math.ceil(engine.timeRemaining % 60);
        sc.fillText(`${mins}:${secs.toString().padStart(2, '0')}`, W / 2, 22);

        // Kill feed
        engine.killFeed.slice(0, 3).forEach((entry, i) => {
            sc.globalAlpha = 0.4 - i * 0.1;
            sc.font = '9px monospace';
            sc.textAlign = 'center';
            sc.fillStyle = '#fff';
            sc.fillText(`${entry.killer} killed ${entry.victim}`, W / 2, 38 + i * 12);
        });
        sc.globalAlpha = 1;

        // ============================================================
        // 3. COMPOSITE — apply light mask
        // ============================================================
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, W, H);
        ctx.drawImage(sceneCanvas, 0, 0);
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(lightCanvas, 0, 0);
        ctx.globalCompositeOperation = 'source-over';

        // ============================================================
        // 4. POST-PROCESSING — film grain + scanlines + vignette
        // ============================================================

        // Subtle scanlines
        ctx.fillStyle = 'rgba(0,0,0,0.04)';
        for (let y = 0; y < H; y += 2) {
            ctx.fillRect(0, y, W, 1);
        }

        // Film grain (sparse random dots)
        ctx.fillStyle = 'rgba(255,255,255,0.01)';
        for (let i = 0; i < 80; i++) {
            const gx = Math.random() * W;
            const gy = Math.random() * H;
            ctx.fillRect(gx, gy, 1, 1);
        }

        // Heavy vignette
        const vigGr = ctx.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.15, W / 2, H / 2, Math.min(W, H) * 0.7);
        vigGr.addColorStop(0, 'rgba(0,0,0,0)');
        vigGr.addColorStop(0.7, 'rgba(0,0,0,0.3)');
        vigGr.addColorStop(1, 'rgba(0,0,0,0.7)');
        ctx.fillStyle = vigGr;
        ctx.fillRect(0, 0, W, H);

        // Screen flash on hit (VERY visible red pulse)
        if (screenFlashRef.current > 0) {
            const flashAlpha = (screenFlashRef.current / 30) * 0.6;
            ctx.fillStyle = `rgba(255,30,30,${flashAlpha})`;
            ctx.fillRect(0, 0, W, H);
            screenFlashRef.current--;
        }
    }, []);

    const handleStartGame = () => {
        if (socket) {
            socket.emit('game_start', { popup_id: popupId });
        }
    };

    const handlePlayAgain = () => {
        setGameState('lobby');
        setResults(null);
        setLocalHP(GAME_CONFIG.PLAYER_MAX_HP);
        setLocalKills(0);
        setKillFeed([]);
        engineRef.current = new GameEngine();
        engineRef.current.localPlayerId = user.id;
        if (socket) {
            socket.emit('game_join', { popup_id: popupId, user_id: user.id, username: user.username });
        }
    };

    // ============================================================
    // LOBBY VIEW
    // ============================================================
    if (gameState === 'lobby') {
        return (
            <div className="flex flex-col items-center justify-center h-full" style={{ background: '#000', minHeight: '500px' }}>
                <div className="text-center mb-8">
                    <h1 className="text-5xl font-black mb-2" style={{
                        color: '#fff',
                        textShadow: '0 0 30px rgba(255,255,255,0.3), 0 0 60px rgba(255,255,255,0.1)',
                        letterSpacing: '8px',
                        fontFamily: 'monospace'
                    }}>
                        BLACK<span style={{ opacity: 0.4 }}>BOX</span>
                    </h1>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'monospace', letterSpacing: '3px' }}>
                        SURVIVE THE DARK
                    </p>
                </div>

                <div className="rounded p-6 mb-6 w-72" style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)'
                }}>
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>
                            OPERATORS
                        </span>
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
                            {players.length}
                        </span>
                    </div>
                    <div className="space-y-2">
                        {players.map((p) => (
                            <div key={p.id} className="flex items-center gap-3 py-1.5 px-3 rounded"
                                style={{ background: 'rgba(255,255,255,0.03)' }}>
                                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#fff', opacity: 0.5 }} />
                                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'monospace' }}>
                                    {p.username} {p.id === user.id && '(you)'}
                                </span>
                            </div>
                        ))}
                        {players.length === 0 && (
                            <p className="text-center py-4" style={{ color: 'rgba(255,255,255,0.15)', fontSize: '11px', fontFamily: 'monospace' }}>
                                waiting...
                            </p>
                        )}
                    </div>
                </div>

                {/* Skin Selector */}
                <div className="rounded p-4 mb-4 w-72" style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)'
                }}>
                    <div className="text-xs font-semibold mb-3" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace', textAlign: 'center' }}>
                        SELECT OPERATIVE
                    </div>
                    <div className="flex items-center justify-center gap-4">
                        <button
                            onClick={() => setSelectedSkin((selectedSkin - 1 + SKINS.length) % SKINS.length)}
                            style={{
                                background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)',
                                cursor: 'pointer', fontSize: '18px', padding: '4px'
                            }}
                        >◀</button>
                        <div className="flex flex-col items-center">
                            <div style={{
                                width: 64, height: 64, borderRadius: '50%',
                                background: `radial-gradient(circle, ${SKINS[selectedSkin].glow}20 0%, transparent 70%)`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: `2px solid ${SKINS[selectedSkin].glow}40`,
                                marginBottom: 8,
                                boxShadow: `0 0 20px ${SKINS[selectedSkin].glow}30`
                            }}>
                                <div style={{
                                    width: 28, height: 28, borderRadius: '50%',
                                    background: SKINS[selectedSkin].body,
                                    border: `2px solid ${SKINS[selectedSkin].outline}`,
                                    boxShadow: `0 0 8px ${SKINS[selectedSkin].glow}60, inset 0 0 5px ${SKINS[selectedSkin].glow}30`,
                                    position: 'relative'
                                }}>
                                    {/* Eyes */}
                                    <div style={{
                                        position: 'absolute', top: '30%', left: '25%',
                                        width: 4, height: 4, borderRadius: '50%',
                                        background: SKINS[selectedSkin].eye,
                                        boxShadow: `0 0 6px ${SKINS[selectedSkin].eye}`
                                    }} />
                                    <div style={{
                                        position: 'absolute', top: '30%', right: '25%',
                                        width: 4, height: 4, borderRadius: '50%',
                                        background: SKINS[selectedSkin].eye,
                                        boxShadow: `0 0 6px ${SKINS[selectedSkin].eye}`
                                    }} />
                                </div>
                            </div>
                            <div style={{ color: SKINS[selectedSkin].glow, fontFamily: 'monospace', fontSize: '13px', fontWeight: 'bold', letterSpacing: '2px' }}>
                                {SKINS[selectedSkin].name.toUpperCase()}
                            </div>
                            <div style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'monospace', fontSize: '9px', marginTop: 2 }}>
                                {SKINS[selectedSkin].desc}
                            </div>
                        </div>
                        <button
                            onClick={() => setSelectedSkin((selectedSkin + 1) % SKINS.length)}
                            style={{
                                background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)',
                                cursor: 'pointer', fontSize: '18px', padding: '4px'
                            }}
                        >▶</button>
                    </div>
                    {/* Skin dots */}
                    <div className="flex justify-center gap-2 mt-3">
                        {SKINS.map((skin, i) => (
                            <div key={skin.id}
                                onClick={() => setSelectedSkin(i)}
                                style={{
                                    width: 8, height: 8, borderRadius: '50%',
                                    background: i === selectedSkin ? skin.glow : 'rgba(255,255,255,0.1)',
                                    cursor: 'pointer',
                                    boxShadow: i === selectedSkin ? `0 0 6px ${skin.glow}` : 'none',
                                    transition: 'all 0.3s'
                                }}
                            />
                        ))}
                    </div>
                </div>

                <div className="text-center mb-6" style={{ color: 'rgba(255,255,255,0.15)', fontSize: '10px', fontFamily: 'monospace' }}>
                    <p>WASD move · mouse aim · click shoot · ESC exit</p>
                </div>

                <div className="flex gap-3">
                    <button onClick={handleStartGame}
                        className="px-8 py-2.5 rounded font-bold text-xs transition-all duration-300 hover:scale-105"
                        style={{
                            background: '#fff',
                            color: '#000',
                            border: 'none',
                            cursor: 'pointer',
                            fontFamily: 'monospace',
                            letterSpacing: '2px'
                        }}
                        disabled={players.length < 1}
                    >
                        {players.length >= 2 ? 'DEPLOY' : 'SOLO RUN'}
                    </button>
                    <button onClick={onExit}
                        className="px-6 py-2.5 rounded font-bold text-xs transition-all duration-300"
                        style={{
                            background: 'transparent',
                            color: 'rgba(255,255,255,0.3)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            cursor: 'pointer',
                            fontFamily: 'monospace'
                        }}
                    >
                        ABORT
                    </button>
                </div>

                {countdown !== null && (
                    <div className="fixed inset-0 flex items-center justify-center z-50"
                        style={{ background: 'rgba(0,0,0,0.9)' }}>
                        <span className="text-8xl font-black" style={{
                            color: '#fff',
                            textShadow: '0 0 60px rgba(255,255,255,0.5)',
                            fontFamily: 'monospace'
                        }}>
                            {countdown > 0 ? countdown : 'GO'}
                        </span>
                    </div>
                )}
            </div>
        );
    }

    // ============================================================
    // GAME OVER VIEW
    // ============================================================
    if (gameState === 'ended' && results) {
        const sortedPlayers = [...(results.players || [])].sort((a, b) => b.kills - a.kills);
        return (
            <div className="flex flex-col items-center justify-center h-full" style={{ background: '#000', minHeight: '500px' }}>
                <h2 className="text-3xl font-black mb-2" style={{
                    color: '#fff',
                    textShadow: '0 0 20px rgba(255,255,255,0.3)',
                    fontFamily: 'monospace',
                    letterSpacing: '4px'
                }}>
                    MISSION END
                </h2>
                <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace' }}>
                    {results.winner} survived
                </p>

                <div className="rounded p-6 mb-6 w-72" style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)'
                }}>
                    <div className="text-xs font-semibold mb-3" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>
                        RESULTS
                    </div>
                    {sortedPlayers.map((p, i) => (
                        <div key={i} className="flex items-center justify-between py-2 px-3 mb-1 rounded"
                            style={{ background: i === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.01)' }}>
                            <div className="flex items-center gap-2">
                                <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '10px', fontFamily: 'monospace' }}>#{i + 1}</span>
                                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace' }}>{p.username}</span>
                                {!p.alive && <span style={{ color: 'rgba(255,255,255,0.15)', fontSize: '10px' }}>✕</span>}
                            </div>
                            <span className="text-xs font-bold" style={{ color: '#fff', fontFamily: 'monospace' }}>{p.kills}</span>
                        </div>
                    ))}
                </div>

                <div className="flex gap-3">
                    <button onClick={handlePlayAgain}
                        className="px-6 py-2.5 rounded font-bold text-xs"
                        style={{
                            background: '#fff', color: '#000',
                            border: 'none', cursor: 'pointer',
                            fontFamily: 'monospace', letterSpacing: '2px'
                        }}>
                        REDEPLOY
                    </button>
                    <button onClick={onExit}
                        className="px-6 py-2.5 rounded font-bold text-xs"
                        style={{
                            background: 'transparent',
                            color: 'rgba(255,255,255,0.3)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            cursor: 'pointer', fontFamily: 'monospace'
                        }}>
                        ABORT
                    </button>
                </div>
            </div>
        );
    }

    // ============================================================
    // GAME PLAYING VIEW — pure canvas
    // ============================================================
    return (
        <div className="relative w-full h-full flex flex-col" style={{ background: '#000' }}>
            <button onClick={onExit}
                className="absolute bottom-3 right-3 z-10 px-3 py-1.5 rounded text-xs"
                style={{
                    background: 'rgba(0,0,0,0.5)',
                    color: 'rgba(255,255,255,0.15)',
                    border: '1px solid rgba(255,255,255,0.03)',
                    cursor: 'pointer', fontFamily: 'monospace'
                }}>
                ESC
            </button>

            <canvas
                ref={canvasRef}
                width={GAME_CONFIG.MAP_WIDTH}
                height={GAME_CONFIG.MAP_HEIGHT}
                className="w-full h-full"
                style={{ cursor: 'crosshair', objectFit: 'contain', background: '#000' }}
            />
        </div>
    );
};

export default BlackBoxGame;
